import { Badge } from "@/components/ui/baseUi/badge"
import { Button } from "@/components/ui/baseUi/button"
import { Text } from "@/components/ui/baseUi/text"
import type { BoxSessionDetailsEntity } from "@/modules/boxSlotSession/entities/boxSessionEntity"
import { MediaType } from "@/modules/common/media/mediaTypes"
import { useLanguage } from "@/modules/language/hooks/useLanguage"
import { cn } from "@/modules/lib/utils"
import { ProfileAvatar } from "@/modules/profile/common/components/profileAvatar"
import { PublicProfileInfoResume } from "@/modules/profile/publicProfile/components/publicProfileInfoResume"
import { useAthleteScoreboard } from "@/modules/scores/scoreboard/hooks/useScoreboard"
import { createScoreboardIndivViewModel } from "@/modules/scores/scoreboard/models/scoreboardIndivViewModel"
import { ScoreDifficultyEnum } from "@/modules/scores/scoreTypes"
import type { ProgramInfiniteSessionEntity } from "@/modules/sessions/entities/programInfiniteSessionEntity"
import type { ProgramOnOffSessionEntity } from "@/modules/sessions/entities/programOnOffSessionEntity"
import type { SessionBlockExerciseEntity } from "@/modules/sessions/entities/sessionBlockExerciseEntity"
import { getDisplayExerciseCategory } from "@/modules/sessions/sessionDetails/blocks/exerciseBlock/exerciseUtils"
import { MediaDetailsImage } from "@/modules/sessions/sessionDetails/blocks/mediaBlock/components/mediaDetailsImage"
import { MediaDetailsPdf } from "@/modules/sessions/sessionDetails/blocks/mediaBlock/components/mediaDetailsPdf"
import { MediaDetailsVideo } from "@/modules/sessions/sessionDetails/blocks/mediaBlock/components/mediaDetailsVideo"
import { SessionBlockType } from "@/modules/sessions/sessionDetails/sessionBlockTypes"
import { ReactionDisplay } from "@/modules/social/components/reactionDisplay"
import { SocialCommentInput } from "@/modules/social/components/socialCommentInput"
import { SocialCommentList } from "@/modules/social/components/socialCommentList"
import { SocialReactionPicker } from "@/modules/social/components/socialReactionPicker"
import { useUserExerciseSocialPost } from "@/modules/social/hooks/useSocialPost"
import { useToggleSocialReaction } from "@/modules/social/hooks/useSocialReactions"
import { createSocialPostModel } from "@/modules/social/models/socialPostModel"
import { useUser } from "@/modules/users/hooks/useUser"
import { useUnit } from "@/modules/utils/hooks/useUnit"
import { word } from "@modules/core/utils/i18n"
import { useSidebar } from "@modules/sidebar/hooks/useSidebar"
import { LoadingSpinner } from "@modules/ui/components/loading"
import { VimeoPlayer } from "@modules/utils/components/vimeoVideo"
import { YouTubeVideoPlayer } from "@modules/utils/components/youTubeVideo"
import { convertYoutubeLinkToEmbed, isVimeoLink, isYoutubeLink } from "@modules/utils/mediaUtils"
import { StarIcon } from "lucide-react"
import { useEffect, useRef, useState } from "react"

type ScoreboardIndivViewProps = {
  session: ProgramInfiniteSessionEntity | ProgramOnOffSessionEntity | BoxSessionDetailsEntity
  initialSelectedExercise: SessionBlockExerciseEntity
  athleteId: string
  className?: string
} & React.ComponentProps<"div">

export const ScoreboardIndivView: React.FC<ScoreboardIndivViewProps> = ({
  session,
  initialSelectedExercise,
  athleteId,
  className,
  ...props
}) => {
  const [selectedExercise, setSelectedExercise] = useState<SessionBlockExerciseEntity>(initialSelectedExercise)
  const commentInputRef = useRef<HTMLTextAreaElement>(null)
  const [parentCommentId, setParentCommentId] = useState<string | null>(null)
  const [replyTo, setReplyTo] = useState<string>()
  const [autoExpandCommentId, setAutoExpandCommentId] = useState<string | null>(null)

  useEffect(() => {
    setParentCommentId(null)
    setAutoExpandCommentId(null)
  }, [selectedExercise.id])

  const { data: userScore } = useAthleteScoreboard(session.id, athleteId)

  const selectedExerciseScore = userScore?.scores.find((score) => score.exerciseId === selectedExercise.id)
  const { sidebarNavigateTo } = useSidebar()
  const user = useUser()
  const unit = useUnit()
  const language = useLanguage()
  const {
    displayScoreTypeCaption,
    displayedScore,
    displayedScoreCompletionDifficulty,
    isScoreCompletionPersonalRecord,
    displayedScoreSetCompletionList,
  } = createScoreboardIndivViewModel(selectedExercise, selectedExerciseScore?.score ?? null, unit, language)
  const isMyRanking = userScore?.user?.id === user?.id ? true : false

  const onCommentSubmit = () => {
    setAutoExpandCommentId(parentCommentId)
    setParentCommentId(null)
    setReplyTo(undefined)
  }

  const onCommentReply = (commentId: string, username?: string) => {
    setParentCommentId(commentId)
    setReplyTo(username)
    commentInputRef.current?.focus()
  }

  const onCancelReply = () => {
    setParentCommentId(null)
    setReplyTo(undefined)
  }

  const handleNavigateToPublicProfile = () => {
    if (userScore?.user && !isMyRanking) {
      void sidebarNavigateTo({
        content: <PublicProfileInfoResume userId={userScore.user.id} />,
      })
    }
  }

  const { data: post, isLoading } = useUserExerciseSocialPost(athleteId, selectedExercise.id)

  const { addReaction, removeReaction, isPending } = useToggleSocialReaction({
    socialPostId: post?.id ?? null,
    sessionId: null,
    userId: athleteId,
    exerciseId: selectedExercise.id,
  })

  const postModel = post ? createSocialPostModel(post) : null

  if (!userScore || isLoading) {
    return <LoadingSpinner />
  }

  return (
    <div className={cn("flex h-full w-full flex-col", className)} {...props}>
      <div className="flex flex-1 flex-col gap-8.5 overflow-y-auto pt-18 pb-4">
        <div className="flex flex-col gap-2.5 px-6">
          <Text fontStyle="TL" className="break-all text-textDefault-heading">
            {word("scoreboard.individual.title", { name: userScore.user?.username })}
          </Text>

          <div className="flex flex-row flex-wrap gap-2">
            {session.blocks
              .filter((block) => block.type === SessionBlockType.SESSION_EXERCISE)
              .map((exerciseBlock) => (
                <Button
                  key={exerciseBlock.content.id}
                  variant={selectedExercise.id === exerciseBlock.content.id ? "primary" : "outline"}
                  onClick={() => {
                    setSelectedExercise(exerciseBlock.content)
                  }}
                  size="sm"
                  disabled={
                    !userScore.scores.find((score) => score.exerciseId === exerciseBlock.content.id)?.score ||
                    userScore.scores.find((score) => score.exerciseId === exerciseBlock.content.id)?.score
                      ?.valueDecimal === null
                  }
                >
                  {getDisplayExerciseCategory(exerciseBlock.content.category, exerciseBlock.content.otherCategory)}
                </Button>
              ))}
          </div>
        </div>

        <div className="flex w-full flex-col gap-3 rounded-b-4xl bg-linear-to-b from-white-50 to-surface-background-secondary px-12 pt-4 pb-4">
          <div className="flex flex-col justify-between gap-2">
            <div className="flex flex-row items-center justify-between gap-2">
              <div className="flex flex-row items-center gap-2">
                <Text fontStyle="HM" className="text-textOnSurface-default">
                  {displayedScore}
                </Text>
                {displayScoreTypeCaption && (
                  <Text fontStyle="BM" className="text-textOnSurface-muted">
                    {displayScoreTypeCaption}
                  </Text>
                )}
              </div>

              <div className="flex flex-row items-center gap-2">
                {isScoreCompletionPersonalRecord && (
                  <Badge variant="primaryIntense" size="md" className="pr-2 italic">
                    <StarIcon className="currentColor" />
                    {word("scoreboard.personalRecord.shortLabel")}
                  </Badge>
                )}

                {displayedScoreCompletionDifficulty && (
                  <Badge
                    variant={
                      selectedExerciseScore?.score?.difficulty === ScoreDifficultyEnum.RX
                        ? "primaryIntense"
                        : "neutralIntense"
                    }
                    size="md"
                    className="italic"
                  >
                    {displayedScoreCompletionDifficulty}
                  </Badge>
                )}
              </div>
            </div>

            {displayedScoreSetCompletionList && displayedScoreSetCompletionList.length > 1 && (
              <div className="grid grid-cols-3 justify-between gap-2">
                {displayedScoreSetCompletionList.map((scoreSetCompletion, index) => (
                  <Text fontStyle="BXS" key={index} className="">
                    {index + 1}. {scoreSetCompletion}
                  </Text>
                ))}
              </div>
            )}
          </div>

          {selectedExerciseScore?.score?.note && (
            <Text fontStyle="BS" className="mt-2 items-stretch text-textOnSurface-subtle">
              {selectedExerciseScore.score.note}
            </Text>
          )}

          {selectedExerciseScore?.score?.medias && selectedExerciseScore.score.medias.length > 0 && (
            <div className="mt-4 flex flex-row flex-wrap items-center justify-start gap-2">
              {selectedExerciseScore.score.medias.map((media, index) => (
                <div
                  key={index}
                  className="flex flex-col overflow-hidden rounded-md border border-neutral-300"
                  style={{ height: media.type === MediaType.S3_VIDEO ? "auto" : 96 }}
                >
                  {media.type === MediaType.S3_VIDEO ? (
                    <MediaDetailsVideo key={index} media={media} className="h-auto max-w-24 min-w-32" />
                  ) : media.type === MediaType.S3_IMAGE ? (
                    <MediaDetailsImage key={index} media={media} />
                  ) : media.type === MediaType.S3_PDF ? (
                    <MediaDetailsPdf key={index} media={media} className="max-w-24" />
                  ) : media.type === MediaType.YOUTUBE && media.url && isYoutubeLink(media.url) ? (
                    <YouTubeVideoPlayer
                      key={index}
                      link={convertYoutubeLinkToEmbed(media.url)}
                      width={157}
                      height={96}
                      className="m-auto max-w-max overflow-hidden rounded"
                    />
                  ) : media.type === MediaType.VIMEO && media.url && isVimeoLink(media.url) ? (
                    <VimeoPlayer
                      key={index}
                      id={index.toString()}
                      link={media.url}
                      width={157}
                      height={96}
                      className="m-auto max-w-max overflow-hidden rounded"
                    />
                  ) : (
                    <Text fontStyle="BM" className="wrap-break-word" key={index}>
                      {word("sessionForm.blocks.add_media.missing_error")}
                    </Text>
                  )}
                </div>
              ))}
            </div>
          )}

          <div
            className={cn("flex flex-row items-center gap-2", isMyRanking ? "cursor-default" : "cursor-pointer")}
            onClick={handleNavigateToPublicProfile}
          >
            <ProfileAvatar
              profile={{ avatar: userScore.user?.avatar ?? null, username: userScore.user?.username ?? "" }}
              className="size-6"
            />
            <Text fontStyle="LS" className="text-center text-textOnSurface-muted">
              {userScore.user?.firstname + " " + userScore.user?.lastname}
            </Text>
          </div>

          <div className="mt-2 -mr-8 -ml-6 flex items-center justify-between">
            {postModel?.hasReactions ? (
              <ReactionDisplay
                displayedReactions={postModel.displayedReactions}
                totalReactions={postModel.totalReactions}
                commentsNumber={postModel.totalComments}
              />
            ) : (
              <div />
            )}

            <SocialReactionPicker
              currentEmoji={postModel?.currentUserEmoji ?? null}
              onSelect={(emoji) => addReaction(emoji)}
              onRemove={() => removeReaction()}
              disabled={isPending}
            />
          </div>
        </div>

        <div className="mx-12 flex flex-col gap-4">
          <SocialCommentList
            socialPostId={post?.id ?? null}
            onReply={onCommentReply}
            autoExpandCommentId={autoExpandCommentId}
          />
        </div>
      </div>

      <div className="sticky bottom-0 bg-surface-background-default">
        <SocialCommentInput
          socialPostId={post?.id ?? null}
          parentCommentId={parentCommentId}
          replyTo={replyTo}
          ref={commentInputRef}
          onSubmit={onCommentSubmit}
          onCancelReply={onCancelReply}
          exerciseId={selectedExercise.id}
          userId={userScore.user?.id}
        />
      </div>
    </div>
  )
}
